/**
 * Where a screenshot was taken, from the platform: planet and local area names,
 * the area's faction, and the planet and zone maps with a pin each.
 *
 * Used by the screenshot form (live, as coordinates are typed or pasted) and by
 * the lightbox. Everything shown comes from `/api/platform/locate`; nothing is
 * stored with the screenshot except its area id and coordinates.
 */

import { cleanPlatformName, type PlatformGalaxyFaction } from '@swtorbit/shared-socket-events';
import type { UsePlatformLocate } from '../../../hooks/usePlatformLocate';
import { MapPin } from './MapPin';

const FACTION_LABEL: Record<PlatformGalaxyFaction, string> = {
  both: '⚪ Both factions',
  imperial: '🔴 Imperial',
  republic: '🔵 Republic',
};

const CARD: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--color-white) 5%, transparent)',
  border: '1px solid color-mix(in srgb, var(--color-white) 12%, transparent)',
  borderRadius: '6px',
  padding: '10px 12px',
};

interface PlatformLocationPanelsProps extends UsePlatformLocate {
  /**
   * The screenshot's own faction (what rounds use), shown instead of the area's
   * galaxy faction when given.
   */
  screenshotFaction?: string;
}

export function PlatformLocationPanels({ result, loading, error, screenshotFaction }: PlatformLocationPanelsProps) {
  if (error) {
    return <p className="text-red fs-sm m-0">{error}</p>;
  }
  if (!result) {
    return loading ? <p className="fs-sm text-slate m-0">Locating…</p> : null;
  }

  const planet = result.area.planet?.name ?? null;
  // The zone is the local area the player stood in (e.g. Jundland); fall back to
  // the combat-log area's name when the position has no zone map.
  const local = cleanPlatformName(result.zone?.name ?? result.area.name);
  const place = [planet, local && local !== planet ? local : null].filter(Boolean).join(' · ');
  const faction = screenshotFaction ?? result.area.galaxyFaction;
  const factionLabel = faction ? FACTION_LABEL[faction as PlatformGalaxyFaction] : undefined;

  return (
    <div className="d-flex flex-column gap-xs" style={{ opacity: loading ? 0.6 : 1 }}>
      <div style={CARD}>
        <p className="fs-xs text-slate m-0 text-uppercase font-semibold" style={{ marginBottom: '4px' }}>Location</p>
        <p className="text-white m-0">📍 {place || 'Unknown area'}</p>
        {factionLabel && (
          <p className="fs-sm text-slate m-0" style={{ marginTop: '2px' }}>{factionLabel}</p>
        )}
      </div>
      <MapPin label="Planet map" map={result.world} emptyText="No map for this position." />
      {result.world && (
        <MapPin label="Zone map" map={result.zone} emptyText="No zone map for this position." />
      )}
    </div>
  );
}
